import { SliderModal, BreakPointCondition, VStack, useBreakpointIf } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Button } from 'native-base';
import React from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../../routes/types';
import { useFixedFeeActions } from '../../../store/fixedFee/actions';
import { masterStateSelector } from '../../../store/master/selector';
import { sessionState } from '../../../store/session/atom';

import { AdvisorSelect } from './components/AdvisorSelect';
import { PartnerSelect } from './components/PartnerSelect';
import { PeriodRangeSelect } from './components/PeriodRangeSelect';
import { ReportModalHeader } from './components/ReportModalHeader';

export const FixedFeeReportModal: React.FC = () => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const direction = useBreakpointValue({ base: 'bottom', lg: 'right' } as const);
  const sliderModalWidth = useBreakpointValue({ base: 1, sm: 0.5, lg: 0.3 });
  const { userProfile, isMaster } = useRecoilValue(sessionState);
  const { data: masterData, loading: isMasterLoading } = useRecoilQuery(
    masterStateSelector({ isMaster })
  );

  const { postGenerateReport } = useFixedFeeActions();

  const canSelectBranchAndAdvisor = !!isMaster;
  const directionIsBottom = direction === 'bottom';

  const [loading, setLoading] = React.useState(false);
  const [hasRangeError, setHasRangeError] = React.useState(false);
  const [selectedPartner, setSelectedPartner] = React.useState<number | undefined>(
    userProfile?.idParceiro
  );
  const [selectedAdvisor, setSelectedAdvisor] = React.useState<number | undefined>(
    userProfile?.idAssessorIntranet
  );
  const [selection, setSelection] = React.useState<{
    initialDate?: Date;
    finalDate?: Date;
  }>();

  const partnerOptions = React.useMemo(() => {
    if (!canSelectBranchAndAdvisor) {
      return [
        {
          label:
            userProfile?.nomeParceiro || `Filial ${userProfile?.idFilialParceiroIntranet || ''}`,
          value: `${userProfile?.idParceiro || 0}`,
        },
      ];
    }

    return (masterData?.partnerList || []).map((partner) => ({
      label: partner.name,
      value: partner.id.toString(),
    }));
  }, [
    canSelectBranchAndAdvisor,
    masterData?.partnerList,
    userProfile?.idFilialParceiroIntranet,
    userProfile?.idParceiro,
    userProfile?.nomeParceiro,
  ]);

  const advisorOptions = React.useMemo(() => {
    if (!canSelectBranchAndAdvisor) {
      return [
        {
          label: userProfile?.nomeRecurso || `${userProfile?.idAssessorIntranet || ''}`,
          value: `${userProfile?.idAssessorIntranet || 0}`,
        },
      ];
    }

    return (masterData?.advisorList || [])
      .filter((advisor) => advisor.partnerId === selectedPartner)
      .map((advisor) => ({
        label: advisor.name,
        value: advisor.id.toString(),
      }));
  }, [
    canSelectBranchAndAdvisor,
    masterData?.advisorList,
    selectedPartner,
    userProfile?.idAssessorIntranet,
    userProfile?.nomeRecurso,
  ]);

  const canGenerateReport =
    selectedAdvisor !== undefined &&
    selectedPartner !== undefined &&
    !!selection?.initialDate &&
    !!selection?.finalDate &&
    !hasRangeError;

  const handleClose = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  };

  const handleGenerateReport = async () => {
    if (!canGenerateReport) {
      return;
    }

    setLoading(true);

    postGenerateReport({
      advisorId: selectedAdvisor,
      partnerId: selectedPartner,
      startDate: selection?.initialDate,
      endDate: selection?.finalDate,
    }).finally(() => {
      setLoading(false);
    });
  };

  return (
    <SliderModal
      dismissWhenClickOutside
      onDismiss={handleClose}
      direction={direction}
      width={sliderModalWidth}>
      <VStack
        flex={1}
        borderTopRadius={{ base: 'stack.xxxs', lg: 0 }}
        w="full"
        h="full"
        backgroundColor="neutral.white"
        p="inset.sm"
        justifyContent="space-between"
        space="stack.xxs">
        <VStack zIndex={10} space="stack.xxs">
          <ReportModalHeader
            direction={direction}
            directionIsBottom={directionIsBottom}
            onClose={handleClose}
          />

          <PartnerSelect
            options={partnerOptions}
            selectedPartner={selectedPartner}
            onChange={setSelectedPartner}
            disabled={!canSelectBranchAndAdvisor || isMasterLoading}
          />

          <AdvisorSelect
            options={advisorOptions}
            selectedAdvisor={selectedAdvisor}
            onChange={setSelectedAdvisor}
            disabled={!canSelectBranchAndAdvisor || isMasterLoading || !selectedPartner}
          />

          <PeriodRangeSelect
            forceMobile={isMobile}
            selection={selection}
            onSelectionChange={setSelection}
            hasError={hasRangeError}
            onErrorUpdate={setHasRangeError}
          />
        </VStack>

        <Button
          w="full"
          variant="primary"
          isDisabled={!canGenerateReport}
          onPress={handleGenerateReport}
          isLoading={loading}>
          GERAR RELATÓRIO
        </Button>
      </VStack>
    </SliderModal>
  );
};
